import { useState, useEffect, useMemo, useContext } from 'react';
import apiUser from '../../apiUser';
import { UserContext } from '../../contexts/userProvider';
import DataTable from '../Table';
import LoadingSpinner from '../Loader/load';
import Modal from './Modal';
import { fmtDataHora, avisoDe } from './format';

const LABELS_EVENTO = {
    envio_email: 'Envio de e-mail', mudanca_status: 'Mudança de status',
    aprovacao: 'Aprovação', contestacao: 'Contestação', expiracao: 'Expiração',
    faturamento_auto: 'Faturamento automático', faturamento_manual: 'Faturamento manual',
    reenvio: 'Reenvio', erro_envio: 'ERRO de envio', erro_job: 'ERRO de job',
    config_alterada: 'Configuração alterada', snapshot_atualizado: 'Relatório atualizado',
    ajuste_equip_salvo: 'Ajuste de equipamento', ajuste_equip_removido: 'Ajuste de equipamento removido',
    lembrete_prazo: 'Lembrete (e-mail)', lembrete_expirado: 'Lembrete pós-prazo (e-mail)', erro_lembrete: 'ERRO de lembrete (e-mail)',
    lembrete_prazo_wa: 'Lembrete (WhatsApp)', lembrete_expirado_wa: 'Lembrete pós-prazo (WhatsApp)',
    lembrete_wa_aceito: 'WhatsApp aceito pelo Infinity', erro_lembrete_wa: 'ERRO de lembrete (WhatsApp)',
};

const CLS_EVENTO = {
    erro_envio: 'bg-red-100 text-red-800', erro_job: 'bg-red-100 text-red-800',
    erro_lembrete: 'bg-red-100 text-red-800', erro_lembrete_wa: 'bg-red-100 text-red-800',
    aprovacao: 'bg-blue-100 text-blue-800', contestacao: 'bg-orange-100 text-orange-800',
    expiracao: 'bg-gray-200 text-gray-700',
    faturamento_auto: 'bg-green-100 text-green-800', faturamento_manual: 'bg-green-100 text-green-800',
    envio_email: 'bg-vox-blue-50 text-vox-blue-800', reenvio: 'bg-vox-blue-50 text-vox-blue-800',
    ajuste_equip_salvo: 'bg-amber-100 text-amber-800', ajuste_equip_removido: 'bg-gray-200 text-gray-700',
};

const AuditoriaFat = () => {
    const { setAlert } = useContext(UserContext);
    const [eventos, setEventos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filtroEvento, setFiltroEvento] = useState('');
    const [detalhe, setDetalhe] = useState(null);

    const aviso = avisoDe(setAlert);

    const carregar = async () => {
        setLoading(true);
        try {
            const params = { limite: 300 };
            if (filtroEvento) params.evento = filtroEvento;
            const { data } = await apiUser.get('/faturamento/auditoria', { params });
            if (data.success) setEventos(data.dados || []);
            else aviso(data.message);
        } catch {
            aviso('Erro ao carregar auditoria.');
        }
        setLoading(false);
    };

    useEffect(() => { carregar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtroEvento]);

    const verDetalhe = (id) => {
        const e = eventos.find((x) => String(x.id) === String(id));
        if (e) setDetalhe(e);
    };

    // "Cliente" (sem width) absorve o espaço flexível; as demais têm largura fixa.
    const headers = [
        { label: 'Quando', key: 'quando_fmt', sortable: false, width: 'w-44' },
        {
            label: 'Evento', key: 'evento_label', sortable: false, width: 'w-52',
            render: (item) => (
                <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${CLS_EVENTO[item.evento] || 'bg-gray-100 text-gray-700'}`}>
                    {LABELS_EVENTO[item.evento] || item.evento}
                </span>
            ),
        },
        { label: 'Cliente', key: 'cliente_nome', sortable: false, truncate: true },
        { label: 'Transição', key: 'transicao', sortable: false, width: 'w-52' },
        { label: 'Por', key: 'autor', sortable: false, width: 'w-40', truncate: true },
        { label: 'Ações', key: 'actions', sortable: false, width: 'w-24', actions: { visualizar: verDetalhe } },
    ];

    const data = useMemo(() => eventos.map((e) => ({
        ...e,
        quando_fmt: fmtDataHora(e.created_at),
        evento_label: LABELS_EVENTO[e.evento] || e.evento,
        transicao: e.status_de || e.status_para ? `${e.status_de || '—'} → ${e.status_para || '—'}` : '—',
        autor: e.user_name || (e.user_id ? `#${e.user_id}` : 'sistema/cliente'),
    })), [eventos]);

    const parseDetalhe = (raw) => {
        if (raw == null) return null;
        try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
    };

    return (
        <div className='w-full'>
            <div className='flex items-center justify-between mb-4 flex-wrap gap-2'>
                <select value={filtroEvento} onChange={(e) => setFiltroEvento(e.target.value)}
                    className='border rounded-lg p-2 dark:bg-gray-700 dark:text-white'>
                    <option value=''>Todos os eventos</option>
                    {Object.entries(LABELS_EVENTO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <button onClick={carregar} className='px-4 py-2 bg-gray-200 dark:bg-gray-600 dark:text-white rounded-lg'>Atualizar</button>
            </div>
            {loading ? <LoadingSpinner /> : eventos.length === 0 ? (
                <div className='p-6 text-center text-gray-500'>Nenhum evento registrado.</div>
            ) : (
                <DataTable headers={headers} data={data} perPageItems={20} noSort autoWidth />
            )}

            {detalhe && (
                <Modal
                    title={`Evento #${detalhe.id} — ${LABELS_EVENTO[detalhe.evento] || detalhe.evento}`}
                    subtitle={<>{fmtDataHora(detalhe.created_at)} • ciclo {detalhe.id_ciclo || '—'} • {detalhe.cliente_nome || ''}</>}
                    onClose={() => setDetalhe(null)}
                    footer={
                        <button onClick={() => setDetalhe(null)} className='px-4 py-2 bg-gray-200 dark:bg-gray-600 dark:text-gray-200 rounded-lg'>Fechar</button>
                    }
                >
                    <div className='px-6 py-5'>
                        <pre className='text-xs bg-gray-50 dark:bg-gray-900 dark:text-gray-100 p-4 rounded-lg whitespace-pre-wrap break-words'>
                            {parseDetalhe(detalhe.detalhe) || 'sem detalhe'}
                        </pre>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default AuditoriaFat;
